/**
 * TeamMemberPicker.js — bottom sheet for transferring a chat to any
 * team member in the CRM roster.
 *
 * #162. Sources its list from $team.roster, which the team-directory
 * service hydrates from the proxy's /team-directory endpoint. That
 * endpoint joins the CRM's /crm/team (authoritative team list,
 * including agents who have never logged into the PWA) with the
 * customers row for the owner, and tags each entry with an `active`
 * flag if the agent has logged into the PWA at least once.
 *
 * Props:
 *   - currentAgent : { id, email, label, isYou, found } | null
 *                    — the resolved current assignee (from resolveAgent)
 *                    so the picker can show a context banner when the
 *                    chat is already owned by someone else
 *   - onPick       : (entry) => void
 *                    — fires with the selected roster entry. Callers
 *                    pass the `.email` field to assignChat.
 *   - onRelease    : () => void  (optional)
 *                    — fires when the user picks the "Release to queue"
 *                    entry at the bottom of the list
 *   - onClose      : () => void  — dismiss the sheet
 */

import { html } from 'htm/preact';
import { useStore } from '@nanostores/preact';

import { $team } from '../services/team-directory.js';

function _initials(source) {
  const s = String(source || '').trim();
  if (!s) return '—';
  const parts = s.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

export function TeamMemberPicker({ currentAgent, onPick, onRelease, onClose }) {
  const team = useStore($team);
  const roster = Array.isArray(team.roster) ? team.roster : [];
  const selfEmail = team.selfEmail || '';

  // Highlight which row represents the currently-open session's user.
  // Used so the picker can show a "(you)" suffix next to their entry.
  const isSelf = (entry) => entry.email && selfEmail && entry.email.toLowerCase() === selfEmail.toLowerCase();

  // Which row is the current assignee (for dimming + "current" label).
  const isCurrent = (entry) => {
    if (!currentAgent) return false;
    if (currentAgent.email && entry.email
        && currentAgent.email.toLowerCase() === entry.email.toLowerCase()) return true;
    if (currentAgent.id && entry.id
        && String(currentAgent.id).toLowerCase() === String(entry.id).toLowerCase()) return true;
    return false;
  };

  const showCurrentBanner = !!(currentAgent && !currentAgent.isYou && currentAgent.label);

  return html`
    <div
      class="inbox-sheet-overlay"
      style="display:block;z-index:300"
      onClick=${onClose}
    ></div>
    <div
      class="inbox-sheet inbox-team-picker"
      role="dialog"
      aria-modal="true"
      aria-label="Transfer chat"
      style="position:fixed;left:0;right:0;bottom:0;z-index:301;background:var(--bg-elev-2,#151925);border-top:1px solid rgba(255,255,255,0.08);border-radius:18px 18px 0 0;padding:14px 0 18px;max-height:78vh;display:flex;flex-direction:column;box-shadow:0 -12px 40px rgba(0,0,0,0.55)"
    >
      <div style="width:36px;height:4px;background:rgba(255,255,255,0.18);border-radius:2px;margin:0 auto 12px"></div>

      <div style="padding:0 18px 12px;display:flex;align-items:center;justify-content:space-between;gap:10px">
        <h3 style="margin:0;font-size:1rem;font-weight:600;color:var(--text-primary)">Transfer chat</h3>
        <button
          onClick=${onClose}
          style="background:transparent;border:none;color:var(--text-secondary,#a4adc1);font-size:1.2rem;cursor:pointer;padding:4px 8px;line-height:1"
          aria-label="Close"
        >×</button>
      </div>

      ${showCurrentBanner && html`
        <div
          style="margin:0 18px 10px;padding:8px 12px;background:rgba(96,165,250,0.08);border:1px solid rgba(96,165,250,0.22);border-radius:10px;font-size:0.75rem;color:#93c5fd;line-height:1.35"
          role="status"
        >
          Currently assigned to <b style="color:#dbeafe">${currentAgent.label}</b>
        </div>
      `}

      <div
        style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch"
      >
        ${roster.length === 0 && html`
          <div style="padding:24px 18px;text-align:center;color:var(--text-muted,#7d8799);font-size:0.85rem">
            Loading team…
          </div>
        `}

        ${roster.map(entry => {
          const current = isCurrent(entry);
          const self    = isSelf(entry);
          const label   = entry.name || entry.email || 'Agent';
          const sub     = entry.email && entry.email !== label ? entry.email : (entry.role || '');
          return html`
            <button
              key=${entry.id || entry.email || label}
              onClick=${() => { if (!current) onPick(entry); }}
              disabled=${current}
              style=${`display:flex;align-items:center;gap:12px;width:100%;padding:12px 18px;background:${current ? 'rgba(255,255,255,0.03)' : 'transparent'};border:none;border-top:1px solid rgba(255,255,255,0.05);color:inherit;cursor:${current ? 'default' : 'pointer'};text-align:left;font-family:inherit;${current ? 'opacity:0.55' : ''}`}
            >
              <span style=${`flex-shrink:0;width:34px;height:34px;border-radius:50%;background:${self ? 'rgba(37,211,102,0.18)' : 'rgba(255,255,255,0.07)'};display:flex;align-items:center;justify-content:center;color:${self ? '#25D366' : 'var(--text-primary)'};font-size:0.72rem;font-weight:700;letter-spacing:0.02em`}>
                ${_initials(entry.name || entry.email)}
              </span>
              <div style="flex:1;min-width:0">
                <div style="font-size:0.88rem;font-weight:600;display:flex;align-items:center;gap:6px">
                  <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${label}</span>
                  ${self && html`<span style="font-size:0.65rem;padding:1px 6px;border-radius:8px;background:rgba(37,211,102,0.18);color:#25D366;font-weight:700;letter-spacing:0.02em">YOU</span>`}
                  ${entry.isOwner && !self && html`<span style="font-size:0.62rem;padding:1px 6px;border-radius:8px;background:rgba(96,165,250,0.14);color:#93c5fd;font-weight:700;letter-spacing:0.02em">OWNER</span>`}
                  ${entry.active && !self && !entry.isOwner && html`<span title="Has logged into the inbox" style="width:6px;height:6px;border-radius:50%;background:#25D366;flex-shrink:0"></span>`}
                </div>
                ${sub && html`<div style="font-size:0.72rem;color:var(--text-muted,#7d8799);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${sub}</div>`}
              </div>
              ${current && html`<span style="font-size:0.68rem;padding:2px 8px;border-radius:8px;background:rgba(96,165,250,0.15);color:#93c5fd;font-weight:600;letter-spacing:0.02em;flex-shrink:0">CURRENT</span>`}
            </button>
          `;
        })}

        ${onRelease && html`
          <button
            onClick=${onRelease}
            style="display:flex;align-items:center;gap:12px;width:100%;padding:12px 18px;background:transparent;border:none;border-top:1px solid rgba(255,255,255,0.09);color:var(--text-secondary,#a4adc1);cursor:pointer;text-align:left;font-family:inherit;margin-top:4px"
          >
            <span style="flex-shrink:0;width:34px;height:34px;border-radius:50%;background:rgba(245,158,11,0.14);display:flex;align-items:center;justify-content:center;color:#f59e0b">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18"/>
                <path d="M6 6l12 12"/>
              </svg>
            </span>
            <div style="flex:1;min-width:0">
              <div style="font-size:0.86rem;font-weight:600">Release to queue</div>
              <div style="font-size:0.72rem;color:var(--text-muted,#7d8799)">Unassign — any agent can pick it up</div>
            </div>
          </button>
        `}
      </div>
    </div>
  `;
}
